import React, {
    type ContextType,
    createContext,
    type FC,
    type PropsWithChildren,
    use,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import * as Sentry from "@sentry/react";
import type { GQL } from "@binale-tech/shared";
import { useIntl } from "react-intl";

import { ProgramSettings } from "@domain/models/ProgramSettings";
import { AppError, AppSettingsContext } from "@domain/context/AppSettingsProvider";
import { BuContext, defaultBuContextValue } from "@domain/context/BuContext";
import { BuTaxesSKR } from "@domain/models/BuTaxUtils";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { type ProductKey } from "@domain/models/Product";
import { type SkrConfig, SKRRetriever } from "@domain/context/SKRRetriever/SKRRetriever";
import { UserContext } from "@domain/context/UserProvider";
import {
    companyCreateBank,
    companyCreateKB,
    companyDeleteBank,
    companyDeleteKB,
    companyMarkFavoriteBank,
    companyMarkFavoriteKB,
    companyUpdateBankYearConfig,
    companyUpdateKBYearConfig,
} from "@domain/context/mutations/companyMutations.graphql";
import { getCompanyAccessData } from "@domain/context/queries/ctxQueries.graphql";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { logger } from "@domain/infrastructure/logger";
import { createRefetchGate } from "@domain/context/companyRefetchGate";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";

type CompanyCtx = {
    companyGQL: GQL.ICompany;
    isLoaded: boolean;
    programSettingsProvider: (pk: ProductKey) => ProgramSettings;
};
const defaultState: CompanyCtx = {
    companyGQL: null,
    isLoaded: false,
    programSettingsProvider: (_pk: ProductKey) => ({}),
};
export const CompanyContext = createContext(defaultState);
type CtxCompanyYearData = {
    yearConfig: GQL.ICompanyAccountingYear;
    yearBanks: GQL.ICompanyBank[];
    yearKbs: GQL.ICompanyKasse[];
};
const ctxYearDataDefault: CtxCompanyYearData = {
    yearConfig: null,
    yearBanks: [],
    yearKbs: [],
};
export const CompanyAccountingYearContext = createContext<CtxCompanyYearData>(ctxYearDataDefault);
export const YearPeriodContext = createContext({
    year: new Date().getFullYear(),
    period: new Date().getMonth() + 1,
    onChangeYear: (_v: number) => {},
    onChangePeriod: (_v: number) => {},
});

type Controller = {
    onBankCreate: (data: Omit<GQL.ICompanyCreateBankInput, "companyId" | "year">) => Promise<unknown>;
    onBankUpdate: (data: Omit<GQL.ICompanyUpdateBankInput, "companyId" | "year">) => Promise<unknown>;
    onBankDelete: (data: Omit<GQL.ICompanyNumEntityDeleteInput, "companyId" | "year">) => Promise<unknown>;
    onBankMarkFavorite: (data: { id: string; isFavorite: boolean }) => Promise<unknown>;
    onKBCreate: (data: Omit<GQL.ICompanyCreateKbInput, "companyId" | "year">) => Promise<unknown>;
    onKBUpdate: (data: Omit<GQL.ICompanyUpdateKbInput, "companyId" | "year">) => Promise<unknown>;
    onKBDelete: (data: Omit<GQL.ICompanyNumEntityDeleteInput, "companyId" | "year">) => Promise<unknown>;
    onKBMarkFavorite: (data: { id: string; isFavorite: boolean }) => Promise<unknown>;
    triggerDataRefetch: () => Promise<unknown>;
    updateProductSettings: (productKey: ProductKey, settings: Partial<ProgramSettings>) => Promise<unknown>;
};
export const CompanyControlContext = createContext<Controller>({
    onBankCreate: () => Promise.reject(),
    onBankUpdate: () => Promise.reject(),
    onBankDelete: () => Promise.reject(),
    onBankMarkFavorite: () => Promise.reject(),
    onKBCreate: () => Promise.reject(),
    onKBUpdate: () => Promise.reject(),
    onKBDelete: () => Promise.reject(),
    onKBMarkFavorite: () => Promise.reject(),
    triggerDataRefetch: () => Promise.reject(),
    updateProductSettings: () => Promise.reject(),
});

export const CompanyProvider: FC<PropsWithChildren> = (props) => {
    const { isAuthenticated, selectedCompany, isUserDataLoaded } = use(UserContext);
    const toastSaver = useToastSaver();
    const client = useApolloClient();
    const intl = useIntl();

    const [createBank] = useMutation(companyCreateBank);
    const [updateBank] = useMutation(companyUpdateBankYearConfig);
    const [deleteBank] = useMutation(companyDeleteBank);
    const [createKB] = useMutation(companyCreateKB);
    const [updateKB] = useMutation(companyUpdateKBYearConfig);
    const [deleteKB] = useMutation(companyDeleteKB);
    const [markFavoriteBank] = useMutation(companyMarkFavoriteBank);
    const [markFavoriteKB] = useMutation(companyMarkFavoriteKB);

    // client.query, not useLazyQuery: ~10 callers refetch through one function, and a second lazy execute
    // aborts the first (Apollo 4). The gate decides which result applies instead.
    const refetchGate = useRef(createRefetchGate());
    const selectedCompanyRef = useRef(selectedCompany);
    useEffect(() => {
        selectedCompanyRef.current = selectedCompany;
    }, [selectedCompany]);

    const skrRetriever = useRef(new SKRRetriever());
    const [isLoaded, setLoaded] = useState(false);
    const [companyGQL, setCompanyGQL] = useState<GQL.ICompany>(null);
    const [buContextValue, setBuContextValue] = useState<ContextType<typeof BuContext>>(defaultBuContextValue);
    const [programSettingsProvider, setProgramSettingsProvider] = useState<(pk: ProductKey) => ProgramSettings>(
        () => ({}),
    );
    const [programSettings, setProgramSettings] = useState<Record<ProductKey, ProgramSettings>>();
    const [year, onChangeYear] = useState<number>(new Date().getFullYear());
    const [period, onChangePeriod] = useState<number>(new Date().getMonth() + 1);
    const yearMonthValue: ContextType<typeof YearPeriodContext> = useMemo(
        () => ({
            year,
            period,
            onChangeYear,
            onChangePeriod,
        }),
        [year, period],
    );
    const companyId = companyGQL?.id;
    const companyContextValue: ContextType<typeof CompanyContext> = useMemo(() => {
        return {
            isLoaded,
            companyGQL,
            programSettingsProvider,
        };
    }, [companyGQL, isLoaded, programSettingsProvider]);
    const { setAppError } = use(AppSettingsContext);

    const reset = useCallback(() => {
        setLoaded(false);
        setCompanyGQL(null);
        setBuContextValue(defaultBuContextValue);
        setProgramSettingsProvider(() => {
            return () => ({});
        });
        BuTaxesSKR.setBuTimeframesCacheValue([], null);
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            reset();
        }
    }, [reset, isAuthenticated]);

    useEffect(() => {
        if (!companyGQL?.id) {
            setProgramSettings({} as Record<ProductKey, ProgramSettings>);
            return;
        }

        ProgramSettings.getFields(companyGQL.id).then((res) => {
            setProgramSettings(res);
        });
    }, [companyGQL]);

    useEffect(() => {
        setProgramSettingsProvider(() => {
            return (pk: ProductKey) => programSettings[pk] || {};
        });
    }, [programSettings]);

    const fetchSKRConfiguration = useCallback(
        async (configs: GQL.ICompanyAccountingYear[], shouldLoad: boolean): Promise<SkrConfig> => {
            if (!shouldLoad) {
                return Promise.resolve(defaultBuContextValue);
            }
            return skrRetriever.current.fetch(configs);
        },
        [],
    );

    const reFetchData = useCallback(
        async (opts?: { selectLatestYear?: boolean }) => {
            if (!selectedCompany) {
                setLoaded(true);
                return;
            }
            // A callback created before a company switch must not refetch (or apply) the old company.
            if (selectedCompany !== selectedCompanyRef.current) {
                return;
            }
            const token = refetchGate.current.begin(Boolean(opts?.selectLatestYear));
            let companyData: GQL.ICompany | undefined;
            try {
                const response = await client.query({
                    query: getCompanyAccessData,
                    variables: { id: selectedCompany },
                    fetchPolicy: "no-cache",
                });
                companyData = response.data?.company;
            } catch (e) {
                // RetryLink has already retried network failures; this is final.
                if (!refetchGate.current.isCurrent(token)) {
                    return;
                }
                logger.crit(e instanceof Error ? e : new Error(String(e)));
                if (refetchGate.current.takeSelectLatestYear()) {
                    // The company itself could not be loaded (initial load or switch), so the app cannot continue.
                    setAppError(AppError.FirebaseConnectionError);
                }
                return;
            }
            if (!refetchGate.current.isCurrent(token) || selectedCompany !== selectedCompanyRef.current) {
                return;
            }
            if (!companyData) {
                setTimeout(() => {
                    reFetchData(opts);
                }, 1000);
                return;
            }
            const selectLatestYear = refetchGate.current.takeSelectLatestYear();
            onChangeYear((prevState) => {
                // On company change always jump to the latest accounting year; on a
                // plain data refetch keep the current year unless it left the scope.
                if (selectLatestYear || !companyData.accountingYears.includes(prevState)) {
                    // A company with no accounting year yet - freshly provisioned, before
                    // anyone opened the year form - takes this branch unconditionally,
                    // because [].includes() is always false. `Math.max()` with no
                    // arguments returns -Infinity, and that year is interpolated straight
                    // into query variables: the calendar built `${year}-01-01` and asked
                    // Postgres for "-Infinity-01-01", which it rejects outright.
                    // The current year is the honest default until a real one is configured.
                    return companyData.accountingYears.length
                        ? Math.max(...companyData.accountingYears)
                        : new Date().getFullYear();
                }
                return prevState;
            });
            const configs = companyData?.accountingConfigs ?? [];
            const hasAccounting = ProductAccessUtils.hasCompanyAccountingProducts(companyData);

            try {
                const skr = await fetchSKRConfiguration(configs, hasAccounting);
                BuTaxesSKR.setBuTimeframesCacheValue(skr.companyBuTimeframes, companyData);
                setCompanyGQL(companyData);
                setBuContextValue(skr);
                setLoaded(true);
                Sentry.setTag("company", companyData.id);
            } catch (e) {
                setAppError(AppError.IndexedDB);
            }
        },
        [client, fetchSKRConfiguration, selectedCompany, setAppError],
    );

    useEffect(() => {
        if (companyGQL) {
            Sentry.setTag("company", companyGQL.id);
            Sentry.setTag("year", year);
        } else {
            Sentry.setTag("company", null);
            Sentry.setTag("year", null);
        }
    }, [companyGQL, year]);

    useEffect(() => {
        if (!isUserDataLoaded) {
            return;
        }
        reset();
        reFetchData({ selectLatestYear: true });
        return;
    }, [reFetchData, selectedCompany, isUserDataLoaded, reset]);

    const companyYearConfig: CtxCompanyYearData = useMemo(() => {
        const configs = companyGQL?.accountingConfigs ?? [];
        const yearConfig = configs.find((v) => v.year === year);
        const yearKbs = [...(companyGQL?.kasseList ?? [])].filter((v) => v.year === year);
        const yearBanks = [...(companyGQL?.bankList ?? [])].filter((v) => v.year === year);
        return {
            yearConfig,
            yearKbs,
            yearBanks,
        };
    }, [companyGQL, year]);

    const controller: Controller = useMemo(
        () => ({
            onBankCreate: async (data) => {
                await toastSaver(() =>
                    createBank({
                        variables: {
                            input: {
                                ...data,
                                companyId,
                            },
                        },
                    }),
                );
                reFetchData();
            },
            onBankUpdate: async (data) => {
                await toastSaver(() =>
                    updateBank({
                        variables: {
                            input: {
                                ...data,
                                year,
                                companyId,
                            },
                        },
                    }),
                );
                reFetchData();
            },
            onBankDelete: async (data) => {
                await toastSaver(() =>
                    deleteBank({
                        variables: {
                            input: {
                                ...data,
                                year,
                                companyId,
                            },
                        },
                    }),
                );
                reFetchData();
            },
            onKBCreate: async (data) => {
                await toastSaver(() =>
                    createKB({
                        variables: {
                            input: {
                                ...data,
                                companyId,
                            },
                        },
                    }),
                );
                reFetchData();
            },
            onKBUpdate: async (data) => {
                await toastSaver(() =>
                    updateKB({
                        variables: {
                            input: {
                                ...data,
                                year,
                                companyId,
                            },
                        },
                    }),
                );
                reFetchData();
            },
            onKBDelete: async (data) => {
                await toastSaver(() =>
                    deleteKB({
                        variables: {
                            input: {
                                ...data,
                                year,
                                companyId,
                            },
                        },
                    }),
                );
                reFetchData();
            },
            onBankMarkFavorite: async ({ id, isFavorite }) => {
                await toastSaver(() =>
                    markFavoriteBank({
                        variables: { input: { id, companyId }, isFavorite },
                    }),
                );
                reFetchData();
            },
            onKBMarkFavorite: async ({ id, isFavorite }) => {
                await toastSaver(() =>
                    markFavoriteKB({
                        variables: { input: { id, companyId }, isFavorite },
                    }),
                );
                reFetchData();
            },
            triggerDataRefetch: () => reFetchData(),
            updateProductSettings: (productKey: ProductKey, settings: Partial<ProgramSettings>) => {
                setProgramSettings((prev) => {
                    if (!prev) {
                        return {
                            [productKey]: settings,
                        } as Record<ProductKey, ProgramSettings>;
                    }

                    return {
                        ...prev,
                        [productKey]: {
                            ...prev[productKey],
                            ...settings,
                        },
                    };
                });

                ProgramSettings.updateFields(companyId, productKey, settings).then(() => {});

                return Promise.resolve();
            },
        }),
        [
            reFetchData,
            companyId,
            intl,
            year,
            createBank,
            updateBank,
            deleteBank,
            createKB,
            updateKB,
            deleteKB,
            markFavoriteBank,
            markFavoriteKB,
            toastSaver,
        ],
    );
    return (
        <CompanyContext value={companyContextValue}>
            <BuContext value={buContextValue}>
                <CompanyControlContext value={controller}>
                    <YearPeriodContext value={yearMonthValue}>
                        <CompanyAccountingYearContext value={companyYearConfig}>
                            {props.children}
                        </CompanyAccountingYearContext>
                    </YearPeriodContext>
                </CompanyControlContext>
            </BuContext>
        </CompanyContext>
    );
};
